import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  clearDirectMessageError,
  clearDirectMessages,
  fetchDirectMessageThreadsThunk,
  fetchDirectMessagesThunk,
  markDirectThreadRead,
  sendDirectMessageThunk,
} from '../../redux/communitySlice';
import type { AsyncStatus } from '../../redux/communitySlice';
import type { CommunityDirectMessageThread } from '../../types';
import './DirectMessagesPage.css';

const formatTimestamp = (timestamp?: string | null) => {
  if (!timestamp) {
    return '';
  }
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const now = new Date();
    const sameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (sameDay) {
      return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (error) {
    return '';
  }
};

const DirectMessagesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { threadId } = useParams<{ threadId?: string }>();

  const {
    dmThreads,
    dmThreadsStatus,
    dmThreadsError,
    dmMessages,
    dmSendStatus,
  } = useAppSelector((state) => state.community);
  const currentUserId = useAppSelector((state) => state.auth.user?.id ?? null);

  const [composer, setComposer] = useState('');
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (dmThreadsStatus === 'idle') {
      dispatch(fetchDirectMessageThreadsThunk());
    }
  }, [dispatch, dmThreadsStatus]);

  const activeEntry = threadId ? dmMessages[threadId] : undefined;

  useEffect(() => {
    if (!threadId) {
      return;
    }
    if (!activeEntry || activeEntry.status === 'idle') {
      dispatch(fetchDirectMessagesThunk({ threadId }));
    }
    dispatch(markDirectThreadRead(threadId));
  }, [dispatch, threadId, activeEntry]);

  useEffect(() => {
    if (dmThreadsStatus !== 'succeeded') {
      return;
    }
    if (!threadId && dmThreads.length > 0) {
  navigate(`/app/community/messages/${dmThreads[0].thread_id}`, { replace: true });
    }
  }, [dmThreadsStatus, dmThreads, threadId, navigate]);

  useEffect(() => {
    const container = messageListRef.current;
    if (!container) {
      return;
    }
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }, [threadId, activeEntry?.items?.length]);

  useEffect(() => {
    return () => {
      dispatch(clearDirectMessageError());
    };
  }, [dispatch]);

  const activeThread = useMemo(() => {
    if (!threadId) {
      return null;
    }
    return dmThreads.find((thread) => thread.thread_id === threadId) ?? null;
  }, [threadId, dmThreads]);

  const activeMessages = activeEntry?.items ?? [];
  const activeEntryStatus: AsyncStatus = activeEntry?.status ?? 'idle';
  const sendStatus: AsyncStatus = threadId ? dmSendStatus[threadId] ?? 'idle' : 'idle';
  const sendDisabled = !threadId || !composer.trim() || sendStatus === 'loading';

  const handleSelectThread = (thread: CommunityDirectMessageThread) => {
    if (thread.thread_id === threadId) {
      return;
    }
  navigate(`/app/community/messages/${thread.thread_id}`);
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (sendDisabled || !threadId) {
      return;
    }
    const content = composer.trim();
    if (!content) {
      return;
    }
    try {
      await dispatch(sendDirectMessageThunk({ threadId, content })).unwrap();
      setComposer('');
    } catch (error) {
      console.error('[DirectMessagesPage] failed to send message', error);
    }
  };

  const handleRefreshThreads = () => {
    dispatch(fetchDirectMessageThreadsThunk());
  };

  const renderThreadItem = (thread: CommunityDirectMessageThread) => {
    const isActive = thread.thread_id === threadId;
    const initials = thread.peer.name?.slice(0, 1)?.toUpperCase() || thread.peer.username?.slice(0, 1)?.toUpperCase() || '友';
    return (
      <button
        key={thread.thread_id}
        type="button"
        className={`dm-page__thread${isActive ? ' dm-page__thread--active' : ''}`}
        onClick={() => handleSelectThread(thread)}
      >
        <div className="dm-page__thread-avatar" aria-hidden>
          {thread.peer.avatar_url ? (
            <img src={thread.peer.avatar_url} alt={thread.peer.name ?? thread.peer.username ?? '用户头像'} />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="dm-page__thread-content">
          <div className="dm-page__thread-header">
            <strong>{thread.peer.name || thread.peer.username || '社区用户'}</strong>
            <time>{formatTimestamp(thread.last_message_at || thread.updated_at)}</time>
          </div>
          <p>{thread.last_message_preview || '开始新的私信对话'}</p>
        </div>
        {thread.unread_count > 0 && <span className="dm-page__thread-unread">{thread.unread_count}</span>}
      </button>
    );
  };

  const renderMessages = () => {
    if (!threadId) {
      return <div className="dm-page__blank">选择一位创作者开始交流。</div>;
    }
    if (activeEntryStatus === 'loading' && activeMessages.length === 0) {
      return <div className="dm-page__blank">消息加载中...</div>;
    }
    if (activeEntryStatus === 'failed') {
      return <div className="dm-page__blank dm-page__blank--error">消息加载失败，请稍后再试。</div>;
    }
    if (activeMessages.length === 0) {
      return <div className="dm-page__blank">暂时还没有私信，发送第一条问候吧。</div>;
    }
    return (
      <div ref={messageListRef} className="dm-page__messages">
        {activeMessages.map((message) => {
          const isMine = message.sender_id === currentUserId;
          return (
            <div
              key={message.message_id}
              className={`dm-page__bubble${isMine ? ' dm-page__bubble--self' : ''}`}
            >
              <p>{message.content}</p>
              <time>{formatTimestamp(message.created_at)}</time>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="dm-page">
      <aside className="dm-page__sidebar">
        <header className="dm-page__sidebar-header">
          <div>
            <h1>私信</h1>
            <p>与中医创作者保持联系，交流心得。</p>
          </div>
          <button type="button" onClick={handleRefreshThreads} disabled={dmThreadsStatus === 'loading'}>
            {dmThreadsStatus === 'loading' ? '刷新中...' : '刷新'}
          </button>
        </header>
        {dmThreadsError && <div className="dm-page__error">{dmThreadsError}</div>}
        <div className="dm-page__threads">
          {dmThreadsStatus === 'loading' && dmThreads.length === 0 && (
            <div className="dm-page__blank">正在寻找私信会话...</div>
          )}
          {dmThreadsStatus === 'succeeded' && dmThreads.length === 0 && (
            <div className="dm-page__blank">还没有私信记录，去创作者主页发起交流吧。</div>
          )}
          {dmThreads.map(renderThreadItem)}
        </div>
      </aside>

      <section className="dm-page__conversation">
        <header className="dm-page__conversation-header">
          <div>
            <strong>{activeThread?.peer.name || activeThread?.peer.username || '选择会话'}</strong>
            {activeThread?.peer.username && <span>@{activeThread.peer.username}</span>}
          </div>
          {threadId && (
            <button
              type="button"
              onClick={() => {
                dispatch(clearDirectMessages(threadId));
                dispatch(fetchDirectMessagesThunk({ threadId }));
              }}
              disabled={activeEntryStatus === 'loading'}
            >
              重新加载
            </button>
          )}
        </header>

        <div className="dm-page__conversation-body">
          {renderMessages()}
        </div>

        <form className="dm-page__composer" onSubmit={handleSend}>
          <textarea
            placeholder={threadId ? '输入内容，按回车发送' : '先从左侧选择一个会话'}
            value={composer}
            onChange={(event) => setComposer(event.target.value)}
            disabled={!threadId || sendStatus === 'loading'}
            rows={2}
          />
          <div className="dm-page__composer-actions">
            <button type="submit" disabled={sendDisabled}>
              {sendStatus === 'loading' ? '发送中...' : '发送' }
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default DirectMessagesPage;
